import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench, ArrowLeft, Search, Loader2, ExternalLink, Anchor, Grid3x3, Weight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export const TOOLS = [
  { id: "fissaggi_inclinato", label: "Fissaggi tetto inclinato", desc: "Distinta fissaggi e staffe per falde inclinate.", icon: Anchor },
  { id: "fissaggi_piano", label: "Fissaggi tetto piano", desc: "Distinta strutture per campi su tetto piano.", icon: Grid3x3 },
  { id: "zavorre", label: "Pre-dimensionamento zavorre", desc: "Numero e peso delle zavorre per tetto piano o inclinato.", icon: Weight },
];

const errMsg = (e) => e?.response?.data?.detail || "Errore di comunicazione con rete.torri.solar";

function ToolCards({ onPick }) {
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="tools-cards">
      {TOOLS.map((t) => (
        <Card key={t.id} onClick={() => onPick(t.id)} className="p-6 cursor-pointer hover:border-primary hover:-translate-y-0.5 transition-[transform,border-color]" data-testid={`tool-card-${t.id}`}>
          <t.icon className="h-7 w-7 text-primary mb-3" strokeWidth={1.5} />
          <h2 className="font-heading font-semibold text-lg">{t.label}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
        </Card>
      ))}
    </div>
  );
}

function ConsulenzeList({ tool, onOpen, opening }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    const h = setTimeout(() => {
      api.get("/tools-tecnici/consulenze", { params: { tool, q: q || undefined } }).then((r) => setItems(r.data.items)).catch((e) => { setItems([]); toast.error(errMsg(e)); });
    }, 250);
    return () => clearTimeout(h);
  }, [tool, q]);
  return (
    <Card className="p-4 space-y-3" data-testid="tools-consulenze">
      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o comune" className="pl-9" data-testid="tools-search-input" />
      </div>
      {items === null ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="tools-empty">Nessuna consulenza compatibile: serve un tetto {tool === "zavorre" ? "piano o inclinato" : tool === "fissaggi_inclinato" ? "inclinato" : "piano"} con layout pannelli generato.</p>
      ) : (
        <div className="divide-y divide-border">
          {items.map((c) => {
            const s = c.summary || {};
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`tools-consulenza-${c.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{s.cliente || "Cliente"}</div>
                  <div className="text-xs text-muted-foreground">{[s.comune, s.indirizzo].filter(Boolean).join(" · ")}</div>
                </div>
                <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${s.roof_type === "piano" ? "border-sky-500/40 text-sky-600" : "border-amber-500/40 text-amber-600"}`} data-testid={`tools-roof-type-${c.id}`}>Tetto {s.roof_type}</span>
                <div className="text-xs text-muted-foreground">{s.n_pannelli} pannelli · {s.potenza_kwp} kWp</div>
                <Button size="sm" onClick={() => onOpen(c.id)} disabled={!!opening} data-testid={`tools-open-${c.id}`}>
                  {opening === c.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ExternalLink className="h-4 w-4 mr-1" />} Apri strumento
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ToolFrame({ link, onReload }) {
  return (
    <div className="space-y-2" data-testid="tools-frame-wrap">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span data-testid="tools-link-expiry">Link valido fino al {link.expires_at ? new Date(link.expires_at).toLocaleString("it-IT") : "—"}</span>
        <Button size="sm" variant="outline" onClick={() => window.open(link.url, "_blank", "noopener")} data-testid="tools-open-new-tab"><ExternalLink className="h-4 w-4 mr-1" /> Apri in nuova scheda</Button>
        <Button size="sm" variant="outline" onClick={onReload} data-testid="tools-new-link"><RefreshCw className="h-4 w-4 mr-1" /> Nuovo link</Button>
      </div>
      <iframe title="Strumento tecnico" src={link.url} className="w-full rounded-lg border border-border bg-white" style={{ minHeight: "90vh" }} data-testid="tools-iframe" />
    </div>
  );
}

export default function Tools() {
  const [params, setParams] = useSearchParams();
  const tool = params.get("tool");
  const leadId = params.get("lead");
  const [link, setLink] = useState(null);
  const [opening, setOpening] = useState(null);
  const meta = TOOLS.find((t) => t.id === tool);

  const open = async (id) => {
    setOpening(id);
    try {
      const r = await api.post("/tools-tecnici/link", { tool, lead_id: id, ore: 8 });
      setLink(r.data);
      setParams({ tool, lead: id });
    } catch (e) { toast.error(errMsg(e)); } finally { setOpening(null); }
  };
  useEffect(() => { if (tool && leadId && !link) open(leadId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const back = () => { setLink(null); setParams(link ? { tool } : {}); };
  return (
    <div className="space-y-6" data-testid="tools-page">
      <div className="flex items-start gap-3">
        {tool && <Button variant="ghost" size="icon" onClick={back} data-testid="tools-back"><ArrowLeft className="h-5 w-5" /></Button>}
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Wrench className="h-6 w-6 text-primary" strokeWidth={1.5} /> {meta ? meta.label : "Tools"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{meta ? (link ? "Il calcolo avviene su rete.torri.solar: premi \"Salva consulenza\" per archiviarlo in Consulenze tecniche." : "Scegli la consulenza su cui lavorare.") : "Strumenti tecnici di rete.torri.solar collegati alle consulenze del CRM."}</p>
        </div>
      </div>
      {!meta ? <ToolCards onPick={(id) => setParams({ tool: id })} /> : link ? <ToolFrame link={link} onReload={() => open(leadId)} /> : <ConsulenzeList tool={tool} onOpen={open} opening={opening} />}
    </div>
  );
}
